import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  usePsaCompanyPicklists,
  usePsaPicklists,
  usePutPsaMapping,
  type CustomerSettings,
  type Picklist,
  type PicklistOption,
  type PriorityMap,
  type PsaConnection,
  type PsaMapping,
} from '@/hooks/usePsa.ts';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { PicklistSelect } from './PicklistSelect.tsx';
import { picklistOptions as options, SEVERITIES } from './psaSettings.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';

/**
 * One customer's own ticket settings (docs/PsaIntegration.md, decision 15): where
 * its tickets are filed, how they're classified, the priority each severity
 * takes, and its company's location, contract or site. Anything left on the
 * connection's setting follows the connection, now and later. Statuses, notes and
 * recovery are the connection's alone, since they're how Witina reads the PSA.
 */
export function CustomerTicketSettings({
  connection,
  mapping,
  customerName,
  onDone,
}: {
  connection: PsaConnection;
  mapping: PsaMapping;
  customerName: string;
  onDone: () => void;
}) {
  const picklists = usePsaPicklists(connection.id, true);
  const companyLists = usePsaCompanyPicklists(connection.id, mapping.companyRef, true);
  if (picklists.isLoading || companyLists.isLoading) {
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading {connection.name}’s lists…
      </p>
    );
  }
  if (picklists.isError || companyLists.isError) {
    return (
      <p className="text-sm text-destructive">
        {apiErrorMessage(picklists.error ?? companyLists.error, `Couldn’t read ${connection.name}’s lists.`)}
      </p>
    );
  }
  return (
    <SettingsForm connection={connection} mapping={mapping} customerName={customerName} onDone={onDone}
      picklists={[...(picklists.data ?? []), ...(companyLists.data ?? [])]} />
  );
}

function SettingsForm({
  connection,
  mapping,
  customerName,
  onDone,
  picklists,
}: {
  connection: PsaConnection;
  mapping: PsaMapping;
  customerName: string;
  onDone: () => void;
  picklists: Picklist[];
}) {
  const [draft, setDraft] = useState<CustomerSettings>(() => mapping.extra ?? {});
  const save = usePutPsaMapping(connection.id);
  const set = (patch: Partial<CustomerSettings>) => { setDraft((d) => ({ ...d, ...patch })); };
  const field = (name: string) => `psa-${mapping.id}-customer-${name}`;
  const own = connection.config;
  const priorities = draft.priority_map ?? {};
  const halo = connection.kind === 'halo';

  // What following the connection means, named: "The connection’s: Triage".
  const inherited = (choices: PicklistOption[], value: number | null | undefined) => {
    const label = value == null ? null : choices.find((c) => c.value === String(value))?.label;
    return label ? `The connection’s: ${label}` : 'The connection’s setting';
  };
  const select = (name: string, label: string, list: string, key: keyof CustomerSettings, connectionValue?: number | null) => {
    const choices = options(picklists, list);
    return (
      <PicklistSelect id={field(name)} label={label} choices={choices}
        value={draft[key] as number | null | undefined}
        onChange={(v) => { set({ [key]: v }); }}
        noneLabel={inherited(choices, connectionValue)} />
    );
  };
  const priorityList = halo ? 'priority_id' : 'priority';
  const subIssues = options(picklists, 'subIssueType').filter(
    (o) => draft.issue_type != null && o.parent === String(draft.issue_type),
  );

  return (
    <form
      className="space-y-4 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          { divisionId: mapping.divisionId, companyRef: mapping.companyRef, companyName: mapping.companyName, extra: draft },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="text-xs text-muted-foreground">
        {customerName}’s tickets in {mapping.companyName}. Anything left on the connection’s setting follows the
        connection’s Ticket settings.
      </p>
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="text-sm font-medium mb-1 sm:col-span-2">Where tickets go</legend>
        {halo ? (
          <>
            {select('type', 'Ticket type', 'tickettype_id', 'tickettype_id', own.tickettype_id)}
            {select('team', 'Team', 'team_id', 'team_id', own.team_id)}
            {select('agent', 'Agent', 'agent_id', 'agent_id', own.agent_id)}
            {select('site', 'Site', 'site_id', 'site_id')}
          </>
        ) : (
          <>
            {select('queue', 'Queue', 'queueID', 'queue_id', own.queue_id)}
            {select('category', 'Ticket category', 'ticketCategory', 'ticket_category_id', own.ticket_category_id)}
            {select('type', 'Ticket type', 'ticketType', 'ticket_type', own.ticket_type)}
            {select('source', 'Source', 'source', 'source_id', own.source_id)}
            <PicklistSelect id={field('issue')} label="Issue type" choices={options(picklists, 'issueType')}
              value={draft.issue_type}
              onChange={(v) => { set({ issue_type: v, sub_issue_type: null }); }}
              noneLabel={inherited(options(picklists, 'issueType'), own.issue_type)} />
            {draft.issue_type != null ? (
              <PicklistSelect id={field('subissue')} label="Sub-issue type" choices={subIssues}
                value={draft.sub_issue_type} onChange={(v) => { set({ sub_issue_type: v }); }} noneLabel="None" />
            ) : null}
            {select('location', 'Location', 'location_id', 'location_id')}
            {select('contract', 'Contract', 'contract_id', 'contract_id')}
            <div className="space-y-1">
              <Label htmlFor={field('due')}>Due in (hours)</Label>
              <Input id={field('due')} type="number" min={1} max={8760} className="w-32"
                placeholder={own.due_in_hours != null ? String(own.due_in_hours) : ''}
                value={draft.due_in_hours ?? ''}
                onChange={(e) => { set({ due_in_hours: e.target.value === '' ? null : Number(e.target.value) }); }} />
              <p className="text-xs text-muted-foreground">Empty follows the connection’s.</p>
            </div>
          </>
        )}
      </fieldset>
      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="text-sm font-medium mb-1 sm:col-span-2">Priority for each severity</legend>
        {SEVERITIES.map((s) => {
          const choices = options(picklists, priorityList);
          return (
            <PicklistSelect key={s.key} id={field(`priority-${s.key}`)} label={s.label} choices={choices}
              value={priorities[s.key]} noneLabel={inherited(choices, own.priority_map?.[s.key])}
              onChange={(v) => {
                const next: PriorityMap = Object.fromEntries(
                  Object.entries(priorities).filter(([key]) => key !== s.key),
                );
                if (v != null) next[s.key] = v;
                set({ priority_map: next });
              }} />
          );
        })}
      </fieldset>
      {save.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(save.error, 'Couldn’t save the settings.')}</p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Save {customerName}’s settings
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => { setDraft({}); }}>
          Follow the connection for everything
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}
